/* ── Role-based page access ──────────────────────────────────────────────
   The login response (/api/auth/login/verify/) returns a `permissions` array
   and an `isOwner` flag. Every navigable page declares what it needs here, and
   both the sidebar and the route switch in App.tsx read from this one map, so a
   page can never appear in the nav while its route stays reachable (or vice
   versa).

   Vocabulary below is the full set observed from the API — an owner login
   carries all 15. Two pages have NO permission key at all (not even for an
   owner): Logs and Settings. Those are gated on ownership instead; see
   OWNER_ONLY_PATHS. If the backend later introduces real keys for them, move
   them into PAGE_PERMISSION and delete the fallback. */

import { createContext, useCallback, useContext } from 'react';

export type Permission =
  | 'view_sites'      | 'manage_sites'
  | 'view_tanks'      | 'manage_tanks'
  | 'view_sensors'    | 'manage_sensors'
  | 'view_alerts'     | 'manage_alerts'
  | 'view_analytics'
  | 'export_reports'
  | 'view_deliveries' | 'manage_deliveries'
  | 'view_security'   | 'manage_security'
  | 'manage_users'
  /* Declared by the integration guide for /api/reports/issues/, but ABSENT from
     every login payload observed so far — an owner returns 15 keys and neither
     of these is among them. Recognised here so the gate starts working the
     moment the backend issues them; see canRaiseIssue below. */
  | 'view_issues'     | 'manage_issues';

/** Identity slice the access checks need. Mirrors the fields App.tsx persists. */
export interface AccessContext {
  permissions: string[];
  isOwner?:    boolean;
  role?:       string;
}

/* Pages reachable by everyone who is signed in. The dashboard is the landing
   route, so gating it would strand a user with nowhere to go; its widgets already
   derive from sites/alerts and render empty for a user who can see neither.
   Profile is the user's own record. */
const ALWAYS_ALLOWED = new Set<string>(['/dashboard', '/profile']);

/* Pages with no permission key in the API's vocabulary. Owners always qualify;
   Admin is included so an org admin does not lose company settings.
   Logs is NOT here — /api/reports/logs/ documents view_analytics and lists all
   five roles as callers, so it is gated on that key like any other page. */
const OWNER_ONLY_PATHS = new Set<string>([
  '/settings',
  // Section 2.0 — the SAP connection holds ERP credentials, and the guide is
  // explicit: HasPermission(owner_only=True), "not Admin. Hide the whole area
  // for everyone else." isOrgAdmin is too loose here, so SapTab additionally
  // gates its own body on useOwnerPower.
  '/sap',
]);

/** Single required permission per page. */
const PAGE_PERMISSION: Record<string, Permission> = {
  '/analytics':            'view_analytics',
  '/monitoring/fuel':      'view_tanks',
  '/security':             'view_security',
  '/sites/map':            'view_sites',
  '/sites/infrastructure': 'view_sites',
  '/sites/tankers':        'view_deliveries',
  '/sites/drivers':        'view_deliveries',
  '/devices':              'view_sensors',
  '/users':                'manage_users',
  // Reports is a read surface: Operator reads it but cannot export, so the page
  // is gated on reading alerts and only the Export control needs export_reports.
  '/reports':              'view_alerts',
  '/notifications':        'view_alerts',
  // Alert emails link here (guide §7), so it must be a real, permission-checked route.
  '/alerts':               'view_alerts',
  // /api/reports/logs/ — "Requires permission: view_analytics", callable by
  // Owner, Admin, Manager, Operator and Auditor.
  '/logs':                 'view_analytics',
  // Section C — the equipment registry reads with view_tanks; recording a fuel
  // issue needs manage_deliveries, gated inside the page.
  '/fuel':                 'view_tanks',
};

export function hasPermission(user: AccessContext | null | undefined, permission: Permission): boolean {
  if (!user) return false;
  return Array.isArray(user.permissions) && user.permissions.includes(permission);
}

/** True when the user owns the org, or is an org-level Admin. Page-level only —
    acting on a *user* additionally requires strictly-greater rank; see canActOnRole. */
export function isOrgAdmin(user: AccessContext | null | undefined): boolean {
  return rankOf(user) >= ADMIN_RANK;
}

/** The one gate. Both the sidebar and the route switch call this. */
export function canAccessPath(user: AccessContext | null | undefined, path: string): boolean {
  if (!user) return false;
  if (ALWAYS_ALLOWED.has(path)) return true;
  if (path === '/sap')           return isOwner(user);          // strictly the owner
  if (OWNER_ONLY_PATHS.has(path)) return isOrgAdmin(user);

  const required = PAGE_PERMISSION[path];
  // Unknown path: deny rather than leak a page that was added without a rule.
  if (!required) return false;
  return hasPermission(user, required);
}

/** First page the user may open — used when a denied route needs a destination. */
export function firstAllowedPath(user: AccessContext | null | undefined): string {
  return '/dashboard';
}

/* ── Rank ────────────────────────────────────────────────────────────────
   Permissions say *what* a user may touch; rank says *whom*. The two are
   independent: holding manage_users lets you reach user management at all,
   rank decides which rows inside it you may act on.

   The rule is strictly-greater rank, so peers can never act on each other —
   an Admin cannot edit, deactivate, delete, reset the password of, or create
   another Admin. Only the Owner outranks Admin.

   Manager, Operator and Auditor sit at the same rank deliberately: none of
   them holds manage_users, so they never reach this surface, and if that ever
   changes they still cannot act on one another.

   The Owner is additionally unreachable by construction — they have no
   VendorUser row, so /users/<id>/ cannot address them. The rank check below is
   the client-side echo of that: nothing outranks OWNER_RANK, so an `owner` row
   that somehow appears in a list is inert. */
export type RoleName = 'owner' | 'Admin' | 'Manager' | 'Operator' | 'Auditor';

const OWNER_RANK = 3;
const ADMIN_RANK = 2;
const STAFF_RANK = 1;

const RANK: Record<string, number> = {
  owner:    OWNER_RANK,
  Admin:    ADMIN_RANK,
  Manager:  STAFF_RANK,
  Operator: STAFF_RANK,
  Auditor:  STAFF_RANK,
};

/** Roles an actor may hand out. Always strictly below their own rank, so an
    Admin can create Manager/Operator/Auditor but never another Admin. `owner`
    is never assignable — transferring ownership is its own action. */
export const ASSIGNABLE_BY_RANK: Record<number, RoleName[]> = {
  [OWNER_RANK]: ['Admin', 'Manager', 'Operator', 'Auditor'],
  [ADMIN_RANK]: ['Manager', 'Operator', 'Auditor'],
  [STAFF_RANK]: [],
};

export function rankOfRole(role: string | undefined | null): number {
  return RANK[role ?? ''] ?? STAFF_RANK;
}

/** An actor's own rank. isOwner wins over the role string — the Owner is
    identified by the flag, not by a role value that may read 'Admin'. */
export function rankOf(user: AccessContext | null | undefined): number {
  if (!user) return 0;
  if (user.isOwner === true) return OWNER_RANK;
  return rankOfRole(user.role);
}

export function isOwner(user: AccessContext | null | undefined): boolean {
  return rankOf(user) >= OWNER_RANK;
}

/** May the actor edit / deactivate / delete / reset the password of a user
    holding `targetRole`? Strictly-greater rank, never equal. */
export function canActOnRole(
  user: AccessContext | null | undefined,
  targetRole: string | undefined | null,
): boolean {
  return rankOf(user) > rankOfRole(targetRole);
}

/** Roles this actor may assign, in rank order. */
export function assignableRoles(user: AccessContext | null | undefined): RoleName[] {
  return ASSIGNABLE_BY_RANK[rankOf(user)] ?? [];
}

/* ── Owner-only powers ───────────────────────────────────────────────────
   Five things the Owner holds that an Admin does not and that cannot be
   delegated. Only the first is reachable in the UI today; the rest have no
   endpoint or screen yet and are listed so the set stays explicit. */
export type OwnerPower =
  | 'edit_company_profile'
  | 'manage_admins'
  | 'transfer_ownership'
  | 'close_account'
  | 'billing';

export function hasOwnerPower(user: AccessContext | null | undefined, _power: OwnerPower): boolean {
  return isOwner(user);
}

/* ── Reading permissions inside a page ───────────────────────────────────
   Pages are lazy-loaded several layers below App, so the signed-in identity
   travels by context rather than props. App provides it; any page calls
   useCan() to gate its own create/edit/delete controls.

   Write actions map to the manage_* key for the thing being written. Two have
   no key of their own in the API's vocabulary and borrow the closest owner:
     generators      → manage_sites  (site infrastructure)
     quality reports → manage_tanks  (tank data)
   Replace these if the backend ever issues real keys for them. */
export const PermissionsContext = createContext<AccessContext | null>(null);

export function useAccess(): AccessContext | null {
  return useContext(PermissionsContext);
}

/** Owner-only powers, e.g. `useOwnerPower('edit_company_profile')`. */
export function useOwnerPower(power: OwnerPower): boolean {
  const access = useContext(PermissionsContext);
  return hasOwnerPower(access, power);
}

/** Rank-aware helpers for user management. */
export function useRankRules() {
  const access = useContext(PermissionsContext);
  return {
    assignable: assignableRoles(access),
    canActOn:   (targetRole: string | undefined | null) => canActOnRole(access, targetRole),
  };
}

/**
 * May this user raise and work maintenance issues?
 *
 * The correct key is `manage_issues`, but no role currently receives it, so
 * gating on it alone would ship a button nobody can see. `manage_alerts` is the
 * closest key that actually exists and lands on exactly the right people —
 * owner, Admin, Manager and Operator hold it ("raise and work issues" is in the
 * Operator's own description), while Auditor does not and must stay read-only.
 *
 * DELETE the fallback once the backend issues `manage_issues`.
 */
export function canRaiseIssue(user: AccessContext | null | undefined): boolean {
  return hasPermission(user, 'manage_issues') || hasPermission(user, 'manage_alerts');
}

export function useCanRaiseIssue(): boolean {
  return canRaiseIssue(useContext(PermissionsContext));
}

/** `const can = useCan();` then `can('manage_sites')`. */
export function useCan(): (permission: Permission) => boolean {
  const access = useContext(PermissionsContext);
  return useCallback(
    (permission: Permission) => hasPermission(access, permission),
    [access],
  );
}
